import React, { useState, useEffect } from 'react';
import { customPropertyService } from '../services';
import { DataTable } from '../components';
import { useSideDialog, useModalDialog, useGlobalState, useMessage } from '../hooks';
import { handleError, Role } from '../helpers';

export const CustomProperties = () => {
  const [customProperties, setCustomProperties] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const promise = new Promise((resolve, reject) => {
      customPropertyService
        .getAll()
        .then(res => {
          setCustomProperties(prev => res.custom_properties);
          resolve(res.custom_properties);
        })
        .catch(err => {
          showMessage(err, 'error');
        })
        .finally(res => {
          setLoading(false);
        });
    });

    Promise.all([promise, fetchAssignedAssetsCount()])
      .then(result => {
        updateFieldCount(result[0], result[1]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAssignedAssetsCount = async () => {
    return new Promise((resolve, reject) => {
      customPropertyService
        .getAssignedAssetsCount()
        .then(res => {
          resolve(res.assigned_assets_count);
        })
        .catch(err => {
          return null;
        });
    });
  };

  const updateFieldCount = (data, statistics) => {
    let dataTmp = data;
    let statistic = {};
    let tmp = null;

    let maxValue = 0;

    statistics.forEach(row => {
      if (row.custom_property_value_count > maxValue) {
        maxValue = row.custom_property_value_count;
      }
    });

    data.forEach((row, index) => {
      statistic = statistics.find(x => x.custom_property_id === row.custom_property_id);
      tmp = dataTmp[index];
      dataTmp[index] = {
        ...tmp,
        custom_property_value_count: statistic ? statistic.custom_property_value_count : 0,
        custom_property_value_count_max: maxValue,
      };
    });

    setCustomProperties(prev => [...dataTmp]);
  };

  const addCustomProperty = async data => {
    const custom_properties = { custom_properties: Array.isArray(data) ? data : [data] };

    await customPropertyService
      .create(custom_properties)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editCustomProperty = async data => {
    const custom_properties = { custom_properties: Array.isArray(data) ? data : [data] };

    await customPropertyService
      .update(custom_properties)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteCustomProperty = async data => {
    await customPropertyService
      .destroy({ custom_properties: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  return (
    <div className="CustomProperties wide-margin">
      <DataTable
        columns={[
          {
            id: 'custom_property_type_code',
            name: '',
            type: 'icon',
            tooltip: 'custom_property_type_name',
          },
          {
            id: 'custom_property_name',
            name: 'Name',
            link: '/custom_properties/:custom_property_id',
            tooltip: 'custom_property_description',
            className: 'bold',
          },
          {
            id: 'custom_property_description',
            name: 'Description',
            type: 'description',
            viewMode: 'descriptions',
          },
          {
            id: 'custom_property_type_name',
            name: 'Type',
            className: 'small-text',
            tooltip: 'custom_property_type_description',
            viewMode: 'statistics',
          },
          {
            id: 'custom_property_value_count',
            name: 'Assets',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'custom_property_value_count', max: 'custom_property_value_count_max' },
            viewMode: 'statistics',
          },
          { id: 'custom_property_code', name: 'Code' },
          {
            id: 'custom_property_level_name',
            name: 'Level',
            className: 'small-text',
            tooltip: 'custom_property_level_description',
            viewMode: 'statistics',
          },
          {
            id: 'search_index_type_name',
            name: 'Search Index',
            className: 'small-text',
            tooltip: 'search_index_type_description',
            viewMode: 'statistics',
          },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items =>
              showSideDialog('addCustomProperty', items || selectedItems, addCustomProperty),
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items =>
              showSideDialog('editCustomProperty', items || selectedItems, editCustomProperty),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items =>
              showModalDialog('deleteCustomProperty', items || selectedItems, deleteCustomProperty),
          },
        ]}
        data={customProperties}
        title="Custom Properties"
        idColumn="custom_property_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={editable}
        filterable={true}
        filterObjects={[
          'custom_property_name',
          'custom_property_description',
          'custom_property_type_name',
          'custom_property_code',
          'custom_property_level_name',
        ]}
        showViewModes={true}
      />
    </div>
  );
};
